import { Suspense } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Footer } from '../../../shared/components/Footer';
import { BrandMark } from './BrandMark';
import { useAuth } from '../../auth';
import { useForceLightTheme } from '../../../shared/hooks/useDarkMode';

const NAV_C = {
  ink:    'oklch(0.20 0.012 270)',
  ink2:   'oklch(0.42 0.010 270)',
  accent: 'oklch(0.56 0.19 264)',
};

function TopNavAuth() {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return (
      <Link
        to="/dashboard"
        className="text-sm font-medium rounded-full px-4 py-2 transition-all hover:scale-[1.02]"
        style={{ background: NAV_C.ink, color: 'white' }}
      >
        Dashboard →
      </Link>
    );
  }

  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <Link
        to="/auth/login"
        className="text-sm font-medium rounded-full px-3 sm:px-4 py-2 transition-all hover:scale-[1.02] whitespace-nowrap"
        style={{ background: NAV_C.ink, color: 'white' }}
      >
        Sign in
      </Link>
    </div>
  );
}

function MarketingContentFallback() {
  return (
    <div
      className="flex min-h-[30vh] items-center justify-center"
      aria-label="Loading page"
    >
      <div className="size-6 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

export function MarketingLayoutRoute() {
  // Brochure pages carry their own light-only palettes — see useForceLightTheme.
  useForceLightTheme();
  const location = useLocation();
  const isHome = location.pathname === '/';

  return (
    <div className="relative min-h-screen flex flex-col text-foreground overflow-hidden">
      {/* Ambient glow. Homepage gets a richer dual-radial to match its hero
          backdrop (originally inside the hero <section>, but that cut off
          behind the top bar — so it lives here now). */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{
          height: isHome ? '100vh' : '60vh',
          background: isHome
            ? 'radial-gradient(60% 50% at 50% 0%, oklch(0.92 0.08 264 / 0.6), transparent 70%), radial-gradient(40% 40% at 85% 80%, oklch(0.94 0.06 290 / 0.5), transparent 70%)'
            : 'radial-gradient(45% 35% at 50% 0%, oklch(0.92 0.08 264 / 0.4), transparent 70%)',
        }}
      />

      {/* Top bar — logo left, auth right (on every marketing page incl. home). */}
      <div className="relative z-10 mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2" aria-label="Home">
          <BrandMark height={26} />
        </Link>
        <TopNavAuth />
      </div>

      <main className="relative z-10 flex-1">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Suspense fallback={<MarketingContentFallback />}>
            <Outlet />
          </Suspense>
        </div>
      </main>
      <Footer />
    </div>
  );
}
